import React from "react";
import { Link } from "react-router-dom";
import { CheckIcon } from "./icons";
import { PACKAGE_IMAGES } from "./packageImages";

export default function PricingGrid({
  packages = [],
  onChoosePlan,
  imagesMap = PACKAGE_IMAGES,
  eyebrow = "Pricing",
  heading = (
    <>
      Choose Your <span className="bk-gold-text italic">Website Plan</span>
    </>
  ),
  subheading = "Select a plan to start your build, and we'll guide you through onboarding and next steps.",
  defaultCtaLabel = "Lock in My Build Site",
}) {
  return (
    <section
      id="pricing"
      data-testid="pricing-grid"
      className="relative py-24 md:py-32 bk-glow"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-[11px] uppercase tracking-[0.28em] text-gold/80">{eyebrow}</span>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl tracking-tight">
            {heading}
          </h2>
          <p className="mt-5 text-white/65 text-lg leading-relaxed">
            {subheading}
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-2 xl:grid-cols-4 gap-6">
          {packages.map((p) => {
            const ctaLabel = p.cta_label || defaultCtaLabel;
            const ctaClass = `mt-7 w-full py-3.5 rounded-full text-xs uppercase tracking-[0.22em] font-bold transition-all text-center ${
              p.highlight
                ? "bk-shimmer text-black hover:scale-[1.02]"
                : "bg-white/5 hover:bg-gold hover:text-black text-white border border-white/10 hover:border-gold"
            }`;

            const isButton = !!p.zylvie_url || (p.cta_mode === "button");

            return (
              <div
                key={p.id}
                data-testid={`plan-card-${p.id}`}
                className={`bk-card rounded-2xl p-6 md:p-7 flex flex-col relative ${p.highlight ? "bk-shadow-pop" : ""}`}
              >
                {p.highlight ? (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.28em] font-semibold px-3 py-1 rounded-full bk-shimmer text-black z-10">
                    Most Popular
                  </span>
                ) : null}

                <div className="mb-5 relative aspect-[4/3] rounded-xl overflow-hidden bg-ink-900 border border-white/5">
                  <img
                    src={imagesMap[p.id]}
                    alt={`${p.name} box`}
                    data-testid={`plan-image-${p.id}`}
                    className="absolute inset-0 w-full h-full object-cover hover:scale-[1.04] transition-transform duration-700"
                    loading="lazy"
                  />
                </div>

                <h3 className="font-display text-xl md:text-2xl leading-tight">
                  {p.name}
                </h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed min-h-[44px]">
                  {p.tagline}
                </p>

                <div className="mt-5 flex items-baseline gap-2">
                  <span className="font-display text-4xl bk-gold-text">${p.price_monthly.toLocaleString()}</span>
                  <span className="text-sm text-white/50">/ month</span>
                </div>
                <div className="text-xs text-white/45 mt-1">
                  + ${p.setup_fee.toLocaleString()} setup
                </div>

                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-white/75">
                      <CheckIcon className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {isButton ? (
                  <button
                    type="button"
                    onClick={() => onChoosePlan?.(p)}
                    data-testid={`plan-cta-${p.id}`}
                    className={ctaClass}
                  >
                    {ctaLabel}
                  </button>
                ) : (
                  <Link
                    to={p.checkout_url || "#"}
                    onClick={() => onChoosePlan?.(p)}
                    data-testid={`plan-cta-${p.id}`}
                    className={ctaClass}
                  >
                    {ctaLabel}
                  </Link>
                )}

                {p.details_path ? (
                  <Link
                    to={p.details_path}
                    data-testid={`plan-details-${p.id}`}
                    className="mt-3 text-xs uppercase tracking-[0.22em] text-white/60 hover:text-gold text-center transition-colors"
                  >
                    See full details →
                  </Link>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
